import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  INCIDENT,
  WHY,
  ROOTCAUSE,
  GUARDS,
  CODE,
  METER,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const flash = interpolate(frame, [40, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="autonomy · what it cost" color={COLORS.amber} />
          <Big>
            an automated task burned{" "}
            <span style={{ color: COLORS.amber, opacity: 0.4 + flash * 0.6 }}>
              $55
            </span>
            <br />
            in 5 days — unwatched
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="amber" />
    </AbsoluteFill>
  );
};

/* 02 — INCIDENT (two numbers that disagree) */
export const Scene02Incident: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.incident.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="two meters · one truth" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="amber" title="openrouter · billed" width={460}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 168 }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 96, color: COLORS.amber }}>$55</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>real spend · 5 days</div>
            </div>
          </Panel>
          <Panel accent="cyan" title="my dashboard · logged" width={460}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 168 }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 96, color: COLORS.text }}>$1.80</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>cost_usd · completeAgentRun</div>
            </div>
          </Panel>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 30 }}>
          cost was written only on a <span style={{ color: COLORS.red }}>successful</span> run
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INCIDENT} accent="amber" />
  </AbsoluteFill>
);

/* 03 — WHY (the loop) */
export const Scene03Why: React.FC = () => {
  const frame = useCurrentFrame();
  const c = interpolate(frame, [120, 150], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.why.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the runaway · recurring + cron=NULL" color={COLORS.red} />
          <Panel accent="red" title="mission · Daily Codebase Monitor" width={760}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 23, lineHeight: 2, color: COLORS.text }}>
              <div>schedule_kind = <span style={{ color: COLORS.amber }}>'recurring'</span></div>
              <div>cron = <span style={{ color: COLORS.red }}>NULL</span> <span style={{ color: COLORS.dim }}>← can never reschedule</span></div>
              <div>step <span style={{ color: COLORS.amber }}>send_email</span> → failed <span style={{ color: COLORS.dim }}>every cycle</span></div>
            </div>
          </Panel>
          <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginTop: 34, opacity: c }}>
            <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 96, color: COLORS.red }}>~1,190</span>
            <span style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim }}>refires / 7 days · ~1M tokens each</span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={WHY} accent="red" />
    </AbsoluteFill>
  );
};

/* 04 — ROOT CAUSE (Pause did nothing) */
export const Scene04RootCause: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.rootcause.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the real bug · pause did nothing" color={COLORS.red} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="red" title="what the poller read" width={470}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.95, color: COLORS.text }}>
              <div>.in('state',</div>
              <div style={{ paddingLeft: 26 }}>['planning','executing',</div>
              <div style={{ paddingLeft: 36 }}>'blocked'])</div>
              <div style={{ color: COLORS.dim, marginTop: 6 }}>state only ↑</div>
            </div>
          </Panel>
          <Panel accent="amber" title="what pause set" width={470}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 150 }}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 30, color: COLORS.text }}>status = <span style={{ color: COLORS.amber }}>'paused'</span></div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim, marginTop: 12 }}>never read by pollMissions</div>
            </div>
          </Panel>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.dim, marginTop: 30 }}>
          the loop never actually stopped
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={ROOTCAUSE} accent="red" />
  </AbsoluteFill>
);

/* 05 — GUARDS (three of them, drawn on) */
const GUARD_PIPE: PipeNode[] = [
  { label: "guard 1", value: "PAUSED", sub: "skip status='paused'", accent: "cyan" },
  { label: "guard 2", value: "cron=NULL", sub: "drop unschedulable", accent: "green" },
  { label: "guard 3", value: "RETRY CAP", sub: "3 fails → failed", accent: "lime" },
];
export const Scene05Guards: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.guards.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="poll-missions.ts · three circuit breakers" color={COLORS.green} />
      <div style={{ width: 1640 }}>
        <PipelineGraph nodes={GUARD_PIPE} startFrame={8} stepFrames={26} nodeWidth={300} />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim, marginTop: 46, letterSpacing: "0.04em" }}>
        all three live on master · Run-Now still overrides
      </div>
    </AbsoluteFill>
    <Caption lines={GUARDS} accent="green" />
  </AbsoluteFill>
);

/* 06 — CODE (the real guard) */
const CODE_SRC = `// poll-missions.ts — guards applied BEFORE the limit slice
const eligible = isExplicitRun ? merged : merged.filter((m) => {
  if (isOrchestrationPaused(m)) return false;        // status='paused'
  if (isUnschedulableRecurringLoopRisk(m)) return false; // cron=NULL
  return true;
});

// recurring + cron=NULL = can only refire, never reschedule.
function isUnschedulableRecurringLoopRisk(row) {
  if (row.state === 'executing') return false; // mid-cycle → ends at 'done'
  return row.schedule_kind === 'recurring' && !row.cron;
}

// finalize-step.ts — pure, deterministic retry cap (mig 192)
function decideRetryCap({ currentCount, isToolGrantGap, max }) {
  const newCount = currentCount + 1;
  return { newCount, capped: !isToolGrantGap && newCount >= max }; // max=3
}`;
export const Scene06Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={CODE_SRC}
        language="tsx"
        filename="poll-missions.ts · finalize-step.ts"
        highlightLines={[3, 4, 10, 18]}
        revealFrames={360}
        fontSize={22}
        width={1320}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 07 — METER (wrap the request, not the success) */
export const Scene07Meter: React.FC = () => {
  const frame = useCurrentFrame();
  const x = interpolate(frame, [60, 95], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.meter.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the durable lesson" color={COLORS.lime} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch", marginTop: 6 }}>
          <Panel accent="red" title="meter on SUCCESS" width={480}>
            <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.55, color: COLORS.text, padding: "6px 2px" }}>
              loop · fail · retry never reaches <span style={{ fontFamily: FONTS.mono, color: COLORS.red }}>complete()</span>
              <div style={{ color: COLORS.dim, fontSize: 20, marginTop: 10 }}>→ the priciest pattern is invisible</div>
            </div>
          </Panel>
          <div style={{ opacity: x, alignSelf: "center", fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</div>
          <Panel accent="lime" title="meter on REQUEST" width={480}>
            <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.55, color: COLORS.text, padding: "6px 2px" }}>
              wrap the <span style={{ fontFamily: FONTS.mono, color: COLORS.lime }}>outbound call</span> itself
              <div style={{ color: COLORS.dim, fontSize: 20, marginTop: 10 }}>→ every token counts, fail or not</div>
            </div>
          </Panel>
        </div>
        <div style={{ marginTop: 38 }}>
          <Callout label="trust the provider meter" accent="amber" width={620}>
            treat <b style={{ color: COLORS.text }}>cost_usd</b> as a lower bound — OpenRouter is the real number
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={METER} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={70}>
          A scheduler with a credit card
          <br />
          needs a <span style={{ color: COLORS.amber }}>Pause</span> that
          <br />
          <span style={{ color: COLORS.cyan }}>actually stops it.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · autonomy with guardrails
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
